import React from 'react';
import {
  Users, Briefcase, FileText, Star, MessageSquare, Wallet
} from 'lucide-react';
import PaymentsHealthCard from './PaymentsHealthCard';
import NewsletterStatsCard from './NewsletterStatsCard';
import RevenueCard from './RevenueCard';
import DataCleanupCard from './DataCleanupCard';
import BlogAgentCard from './BlogAgentCard';

export default function OverviewTab({ stats, navigate }) {
  if (!stats) return null;
  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <Users className="h-8 w-8 text-blue-500" />
            <span className="text-sm text-green-600 font-medium">+{stats.users.recent_7_days} this week</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.users.total}</p>
          <p className="text-gray-500 text-sm">Total Users</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <Briefcase className="h-8 w-8 text-green-500" />
            <span className="text-sm text-green-600 font-medium">+{stats.jobs.recent_7_days} this week</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.jobs.total}</p>
          <p className="text-gray-500 text-sm">Total Jobs</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <FileText className="h-8 w-8 text-purple-500" />
            <span className="text-sm text-green-600 font-medium">+{stats.applications.recent_7_days} this week</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.applications.total}</p>
          <p className="text-gray-500 text-sm">Applications</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <Star className="h-8 w-8 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.reviews.total}</p>
          <p className="text-gray-500 text-sm">Reviews</p>
        </div>
      </div>

      <RevenueCard />

      <DataCleanupCard />
      <BlogAgentCard />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-blue-100 rounded-lg"><Users className="h-5 w-5 text-blue-600" /></div>
            <span className="text-gray-600">Workers</span>
          </div>
          <p className="text-2xl font-bold">{stats.users.workers}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-green-100 rounded-lg"><Briefcase className="h-5 w-5 text-green-600" /></div>
            <span className="text-gray-600">Contractors</span>
          </div>
          <p className="text-2xl font-bold">{stats.users.contractors}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-purple-100 rounded-lg"><MessageSquare className="h-5 w-5 text-purple-600" /></div>
            <span className="text-gray-600">Messages</span>
          </div>
          <p className="text-2xl font-bold">{stats.messages.total}</p>
        </div>
      </div>

      <PaymentsHealthCard />

      <NewsletterStatsCard />

      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Job Status Overview</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{stats.jobs.open}</p>
            <p className="text-sm text-gray-600">Open Jobs</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-600">{stats.jobs.closed}</p>
            <p className="text-sm text-gray-600">Closed Jobs</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{stats.applications.accepted}</p>
            <p className="text-sm text-gray-600">Hired</p>
          </div>
        </div>
      </div>

      {stats.escrows && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Wallet className="h-5 w-5 text-orange-500" />
            Escrow Overview
          </h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{stats.escrows.total}</p>
              <p className="text-sm text-gray-600">Total Escrows</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{stats.escrows.funded}</p>
              <p className="text-sm text-gray-600">Active/Funded</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-all" onClick={() => navigate('/admin/disputes')} data-testid="disputes-quick-link">
              <p className="text-2xl font-bold text-red-600">{stats.escrows.disputed}</p>
              <p className="text-sm text-gray-600">Disputed</p>
              <p className="text-[10px] font-bold text-red-700 mt-1 uppercase">Open Queue →</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-all" onClick={() => navigate('/admin/client-errors')} data-testid="client-errors-quick-link">
              <p className="text-2xl font-bold text-purple-700">Bug</p>
              <p className="text-sm text-gray-600">Client Errors</p>
              <p className="text-[10px] font-bold text-purple-700 mt-1 uppercase">View Inbox →</p>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-lg cursor-pointer hover:bg-emerald-100 transition-all" onClick={() => navigate('/admin/verifications')} data-testid="verifications-quick-link">
              <p className="text-2xl font-bold text-emerald-700">Pro</p>
              <p className="text-sm text-gray-600">Verifications</p>
              <p className="text-[10px] font-bold text-emerald-700 mt-1 uppercase">Review Queue →</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{stats.payments.total_transactions}</p>
              <p className="text-sm text-gray-600">Transactions</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
